// The bossa style's band (docs/AUDIO_DESIGN.md §12): upright bass, flute, and a soft kit (surdo kick, brush,
// cross-stick, shaker, tom). Persistent voices gated with setTargetAtTime, like instruments.js: nothing per hit.
import { mtof, hold, noise } from '../dsp.js';
import { LEVELS, gain, filter, osc, panner, persistent } from '../instruments.js';

// Levels (tuning knobs for the mix balance), against the jazzhop bass, lead and kit they replace.
export const BAND = { bass: 0.9, flute: 2.3, kit: 1 };

/* ---------------- upright bass: one periodic-wave oscillator through a plucked LP ---------------- */
/** Harmonics 1 · .42 · .2 · .1 · .05 · .025 (the 2nd–5th carry it on phone speakers) → LP that drops 1.6 kHz → p.lp
 *  (460–600 Hz, τ 90 ms, Q .9). Amplitude: τ 4 ms to 0.5v, to 0.5 × peak (τ 200 ms), then dies away (τ 1.1 s);
 *  release τ 60 ms. Each note cancels the last one's tail (no stray automation). p: { lp } */
export function createUpright(ac, out, p, { registry, tag, t0 = ac.currentTime }) {
  const wave = ac.createPeriodicWave(new Float32Array(7), new Float32Array([0, 1, 0.42, 0.2, 0.1, 0.05, 0.025]));
  const o = osc(ac, 'sine', 55), lp = filter(ac, 'lowpass', p.lp || 520, 0.9), amp = gain(ac, 0);
  o.setPeriodicWave(wave);
  o.connect(lp); lp.connect(amp); amp.connect(out);
  const v = persistent(registry, tag, [o], [o, lp, amp], t0);
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi), A = 0.5 * vel * BAND.bass, off = t + Math.max(0.08, dur);
      for (const prm of [o.frequency, lp.frequency, amp.gain]) hold(prm, t);
      if (slide) { o.frequency.setValueAtTime(f * Math.pow(2, -1 / 12), t); o.frequency.setTargetAtTime(f, t, 0.025); }
      else o.frequency.setValueAtTime(f, t);
      lp.frequency.setTargetAtTime(1600, t, 0.003); lp.frequency.setTargetAtTime(p.lp || 520, t + 0.012, 0.09);
      amp.gain.setTargetAtTime(A, t, 0.004);
      amp.gain.setTargetAtTime(0.5 * A, t + 0.02, 0.2);
      if (off > t + 0.35) amp.gain.setTargetAtTime(0, t + 0.35, 1.1);
      amp.gain.setTargetAtTime(0, off, 0.06);
    },
    dispose(t) { v.dispose(t); },
  };
}

/* ---------------- flute: sine + 2nd harmonic, breath noise and a chiff, delayed vibrato, dotted-8th echo ---------------- */
/** sine f + sine 2f (.12) → LP 3.4 kHz; breath = noise → BP 2.2 kHz (Q .8) at .5 × level for the 30 ms chiff, then .1;
 *  attack τ 35 ms (glide τ 20 ms), release τ 90 ms; vibrato 4.8 Hz ±11 ¢ from 300 ms; echo 3 steps (fb .28, LP 2.4k,
 *  wet .3); pan lead.pan, reverb send .45. */
export function createFlute(ac, out, send, lead, { registry, tag, stepDur, t0 = ac.currentTime }) {
  const nodes = [], N = x => (nodes.push(x), x);
  const o1 = N(osc(ac, 'sine', 440)), o2 = N(osc(ac, 'sine', 880)), g2 = N(gain(ac, 0.12));
  const src = N(ac.createBufferSource()), bp = N(filter(ac, 'bandpass', 2200, 0.8)), breath = N(gain(ac, 0));
  src.buffer = noise(ac, 'white'); src.loop = true;
  const lp = N(filter(ac, 'lowpass', 3400, 0.5)), amp = N(gain(ac, 0)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.45));
  const dly = N(ac.createDelay(2)), dLP = N(filter(ac, 'lowpass', 2400, 0.5)), fb = N(gain(ac, 0.28)), wet = N(gain(ac, 0.3));
  const vib = N(osc(ac, 'sine', 4.8)), vibG = N(gain(ac, 0));
  dly.delayTime.value = Math.min(1.9, 3 * stepDur);
  o1.connect(amp); o2.connect(g2); g2.connect(amp); src.connect(bp); bp.connect(breath); breath.connect(lp); amp.connect(lp);
  lp.connect(pan); lp.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(pan);
  pan.connect(out); pan.connect(sendG); sendG.connect(send);
  vib.connect(vibG); vibG.connect(o1.detune); vibG.connect(o2.detune);
  const v = persistent(registry, tag, [o1, o2, src, vib], nodes, t0);
  return {
    note(t, midi, vel, dur, glide = false) {
      const f = mtof(midi), A = 0.2 * vel * BAND.flute, d = Math.max(0.06, dur);
      for (const prm of [amp.gain, breath.gain, vibG.gain]) hold(prm, t);
      if (glide) { o1.frequency.setTargetAtTime(f, t, 0.02); o2.frequency.setTargetAtTime(2 * f, t, 0.02); }
      else { o1.frequency.setValueAtTime(f, t); o2.frequency.setValueAtTime(2 * f, t); }
      amp.gain.setTargetAtTime(A, t, glide ? 0.02 : 0.035);
      amp.gain.setTargetAtTime(0, t + d, 0.09);
      if (!glide) breath.gain.setTargetAtTime(0.5 * A, t, 0.004);
      breath.gain.setTargetAtTime(0.1 * A, t + 0.03, 0.05);
      breath.gain.setTargetAtTime(0, t + d, 0.07);
      vibG.gain.setTargetAtTime(0, t, 0.02);
      if (d > 0.35) vibG.gain.setTargetAtTime(11, t + 0.3, 0.15);
    },
    dispose(t) { v.dispose(t); },
  };
}

/* ---------------- kit: surdo kick, brush, cross-stick, brushed hat, shaker, tom (one noise source) ---------------- */
/** kit: { kickF0, kickF1, kickDecay, brush, hatHP, hatClosed, rimF, shaker, lp }
 *  kick: sine kickF0 (95–120) → kickF1 (45–53) Hz (τ 40 ms), 0.5v, attack τ 2 ms, decay τ kickDecay (.16–.22 s); felt
 *        beater: noise → BP 1 kHz (Q .8), 0.05v, τ 5 ms
 *  snare lane = brush: noise → BP 3 kHz (Q .45), 0.3v, attack τ 6 ms, decay τ brush (.1–.16 s; ghost 60 ms), send .18
 *  hat: noise → HP hatHP (7–9 kHz), 0.09v, τ hatClosed (9–15 ms; open 90 ms), pan +.22
 *  rim = cross-stick: sine rimF (520–620) .55 + triangle 2.5·rimF .3 + noise BP 2.3 kHz (Q 1.4) .5 → HP 300, 0.32v,
 *        τ 16 ms, pan −.18, send .22
 *  shaker: noise → HP 5.2 kHz → BP 8 kHz (Q .7), 0.13v, attack τ 5 ms, decay τ shaker (30–45 ms), pan +.32
 *  tom (fills, a surdo pickup): sine 150 → 100 Hz (τ 50 ms), 0.45v, τ .2 s, pan −.25, send .15 */
export function createBossaKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime }) {
  const L = LEVELS.kit * BAND.kit, nodes = [], N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  bus.connect(out);
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = N(gain(ac, 0)), kBP = N(filter(ac, 'bandpass', 1000, 0.8)), kClick = N(gain(ac, 0));
  kOsc.connect(kAmp); kAmp.connect(bus); src.connect(kBP); kBP.connect(kClick); kClick.connect(bus);
  const bBP = N(filter(ac, 'bandpass', 3000, 0.45)), bAmp = N(gain(ac, 0)), bSend = N(gain(ac, 0.18));
  src.connect(bBP); bBP.connect(bAmp); bAmp.connect(bus); bAmp.connect(bSend); bSend.connect(send);
  const hHP = N(filter(ac, 'highpass', kit.hatHP, 0.7)), hAmp = N(gain(ac, 0)), hPan = N(panner(ac, 0.22));
  src.connect(hHP); hHP.connect(hAmp); hAmp.connect(hPan); hPan.connect(bus);
  const r1 = N(osc(ac, 'sine', kit.rimF)), r2 = N(osc(ac, 'triangle', 2.5 * kit.rimF)), rg1 = N(gain(ac, 0.55)), rg2 = N(gain(ac, 0.3));
  const rBP = N(filter(ac, 'bandpass', 2300, 1.4)), rg3 = N(gain(ac, 0.5)), rHP = N(filter(ac, 'highpass', 300, 0.7));
  const rAmp = N(gain(ac, 0)), rPan = N(panner(ac, -0.18)), rSend = N(gain(ac, 0.22));
  r1.connect(rg1); r2.connect(rg2); src.connect(rBP); rBP.connect(rg3);
  for (const g of [rg1, rg2, rg3]) g.connect(rHP);
  rHP.connect(rAmp); rAmp.connect(rPan); rPan.connect(bus); rPan.connect(rSend); rSend.connect(send);
  const xHP = N(filter(ac, 'highpass', 5200, 0.7)), xBP = N(filter(ac, 'bandpass', 8000, 0.7)), xAmp = N(gain(ac, 0)), xPan = N(panner(ac, 0.32));
  src.connect(xHP); xHP.connect(xBP); xBP.connect(xAmp); xAmp.connect(xPan); xPan.connect(bus);
  const tOsc = N(osc(ac, 'sine', 100)), tAmp = N(gain(ac, 0)), tPan = N(panner(ac, -0.25)), tSend = N(gain(ac, 0.15));
  tOsc.connect(tAmp); tAmp.connect(tPan); tPan.connect(bus); tPan.connect(tSend); tSend.connect(send);
  const p = persistent(registry, tag, [src, kOsc, r1, r2, tOsc], nodes, t0);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, 0.04);
      hit(kAmp.gain, 0.5 * v * L, t, 0.002, 0.008, kit.kickDecay);
      hit(kClick.gain, 0.05 * v * L, t, 0.0006, 0.002, 0.005);
    },
    snare(t, v, ghost = false) { hit(bAmp.gain, 0.3 * v * L, t, 0.006, 0.015, ghost ? 0.06 : kit.brush); },
    hat(t, v, open = false) { hit(hAmp.gain, 0.09 * v * L, t, 0.0008, 0.003, open ? 0.09 : kit.hatClosed); },
    rim(t, v) { hit(rAmp.gain, 0.32 * v * L, t, 0.0006, 0.002, 0.016); },
    shaker(t, v) { hit(xAmp.gain, 0.13 * v * L, t, 0.005, 0.01, kit.shaker); },
    tom(t, v) {
      tOsc.frequency.setValueAtTime(150, t); tOsc.frequency.setTargetAtTime(100, t, 0.05);
      hit(tAmp.gain, 0.45 * v * L, t, 0.001, 0.006, 0.2);
    },
    dispose(t) { p.dispose(t); },
  };
}
